export type ScrollSubscriber = {
    callback: () => void;
    once?: boolean;
};

type GlobalScroll = {
    disable: (disable: Ref<boolean>) => void;
    subscribe: (subscriber: ScrollSubscriber) => () => void;
    isScrolled: Ref<boolean>;
    direction: Ref<'down' | 'up'>;
};

let scrollTimeout: ReturnType<typeof setTimeout>;
let lastEventTime: number = 0;
let scrollSubscribers: ScrollSubscriber[] = [];

const DIRECTION_THRESHOLD_PX = 5;
let previousY = 0;
const directionRef = ref<'down' | 'up'>('down');

const disable: GlobalScroll['disable'] = (disable) => {
    useHead({
        bodyAttrs: {
            class: computed(() => (disable.value ? 'overflow-hidden' : '')),
        },
    });
};

const handleScrollEvent = () => {
    const nowTime = new Date().getTime();
    // fire event at least every 300ms
    if (nowTime - lastEventTime < 200) clearTimeout(scrollTimeout);
    scrollTimeout = setTimeout(() => {
        lastEventTime = new Date().getTime();

        // compute direction before notifying subscribers
        const currentY = window.scrollY || window.pageYOffset || 0;
        const delta = currentY - previousY;
        if (Math.abs(delta) >= DIRECTION_THRESHOLD_PX) {
            directionRef.value = delta > 0 ? 'down' : 'up';
            previousY = currentY;
        }

        for (const subscriber of scrollSubscribers) {
            subscriber.callback();
            if (subscriber.once) removeSubscriber(subscriber);
        }
    }, 100);
};

const removeSubscriber = (subscriber: ScrollSubscriber) => {
    scrollSubscribers = scrollSubscribers.filter(({ callback }) => subscriber.callback !== callback);
};

const subscribe: GlobalScroll['subscribe'] = (subscriber) => {
    if (scrollSubscribers.length === 0) {
        // initialize baseline before the first listener runs
        previousY = window.scrollY || window.pageYOffset || 0;
        window.addEventListener('scroll', handleScrollEvent, { capture: true, passive: true });
    }
    scrollSubscribers.push(subscriber);

    return () => removeSubscriber(subscriber);
};

export default defineNuxtPlugin(() => {
    const isScrolled = ref<boolean>(false);
    subscribe({
        callback: () => {
            isScrolled.value = window.scrollY > 0;
        },
    });
    useHead({
        bodyAttrs: {
            class: computed(() => {
                const direction = directionRef.value === 'down' ? 'down' : 'up';
                const scrolled = isScrolled.value ? 'scrolled' : '';
                return `${scrolled} ${direction}`.trim();
            }),
        },
    });

    const scroll: GlobalScroll = {
        disable,
        subscribe,
        isScrolled,
        direction: directionRef,
    };

    return {
        provide: {
            scroll,
        },
    };
});
